import React, { useState, useMemo } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import { MdFilterList, MdClose } from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/shared/PageHeader';
import IngestionPage from './IngestionPage';
import MarketFlowsPage from './MarketFlowsPage';
import SectorFlowsPage from './SectorFlowsPage';
import TickerDetailPage from './TickerDetailPage';
import IndexEventsPage from './IndexEventsPage';

const PRESETS = [
  { label: '1W', value: '1w', weeks: 1 },
  { label: '4W', value: '4w', weeks: 4 },
  { label: '13W', value: '13w', weeks: 13 },
  { label: '52W', value: '52w', weeks: 52 },
  { label: 'Custom', value: 'custom', weeks: null },
];

const MARKETS = ['ADX', 'DFM', 'Qatar', 'Kuwait', 'Saudi Arabia'];

const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);

  const toggle = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter((v) => v !== val));
    } else {
      onChange([...selected, val]);
    }
  };

  const allSelected = selected.length === options.length;

  return (
    <div className="relative">
      <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 text-left truncate focus:ring-2 focus:ring-[#c9a96e]/30 focus:border-[#c9a96e] outline-none"
      >
        {allSelected ? 'All' : selected.length === 0 ? 'None' : selected.join(', ')}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[200px] bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          <div
            className="px-3 py-2 text-xs text-[#c9a96e] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700"
            onClick={() => onChange(allSelected ? [] : [...options])}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </div>
          {options.map((opt) => (
            <div
              key={opt}
              className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${
                selected.includes(opt)
                  ? 'bg-[#c9a96e]/5 text-[#c9a96e]'
                  : 'text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/50'
              }`}
              onClick={() => toggle(opt)}
            >
              <span
                className={`w-3.5 h-3.5 rounded border flex items-center justify-center text-[10px] ${
                  selected.includes(opt) ? 'bg-[#c9a96e] border-[#c9a96e] text-white' : 'border-gray-400 dark:border-gray-600'
                }`}
              >
                {selected.includes(opt) && '✓'}
              </span>
              {opt}
            </div>
          ))}
          <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700">
            <button onClick={() => setOpen(false)} className="text-xs text-[#c9a96e] font-medium">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const GccFlowsLayout = () => {
  const { user } = useAuth();
  const isUploader = user?.is_admin || ['mahroof', 'Jithin'].includes(user?.username);

  const tabs = [
    { to: '/gcc', label: 'Market Flows', end: true },
    { to: '/gcc/sectors', label: 'Sector Flows' },
    { to: '/gcc/tickers', label: 'Ticker Detail' },
    { to: '/gcc/events', label: 'Index Events' },
    ...(isUploader ? [{ to: '/gcc/ingestion', label: 'Ingestion' }] : []),
  ];

  const [preset, setPreset] = useState('13w');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [markets, setMarkets] = useState([...MARKETS]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filterParams = useMemo(() => {
    const params = {};
    if (preset !== 'custom') {
      params.preset = preset;
    } else {
      if (customStart) params.date_from = customStart;
      if (customEnd) params.date_to = customEnd;
    }
    if (markets.length > 0 && markets.length < MARKETS.length) {
      params.market = markets.join(',');
    }
    return params;
  }, [preset, customStart, customEnd, markets]);

  const inputCls =
    'bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-[#c9a96e]/30 focus:border-[#c9a96e] outline-none';

  return (
    <div>
      <PageHeader
        title="GCC Daily Flows"
        description="EFG Hermes daily foreign-flow data across ADX, DFM, Qatar, Kuwait, Saudi"
      />

      {/* Filter bar */}
      <div className="sticky top-0 z-30 bg-[#f7f9fc] dark:bg-[#0d1117] pb-2">
        {/* Mobile */}
        <div className="md:hidden flex items-center justify-between mb-2">
          <div className="flex gap-1.5 overflow-x-auto">
            {PRESETS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPreset(p.value)}
                className={`shrink-0 text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${
                  preset === p.value
                    ? 'bg-[#c9a96e] text-white'
                    : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="ml-2 shrink-0 p-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400"
          >
            {filtersOpen ? <MdClose size={18} /> : <MdFilterList size={18} />}
          </button>
        </div>

        {filtersOpen && (
          <div className="md:hidden grid grid-cols-1 gap-3 mb-3 p-3 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg">
            {preset === 'custom' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Start</label>
                  <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className={`${inputCls} w-full`} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">End</label>
                  <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className={`${inputCls} w-full`} />
                </div>
              </div>
            )}
            <MultiSelect label="Markets" options={MARKETS} selected={markets} onChange={setMarkets} />
          </div>
        )}

        {/* Desktop */}
        <div className="hidden md:grid md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Period</label>
            <div className="flex gap-1">
              {PRESETS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => setPreset(p.value)}
                  className={`text-xs font-medium px-2.5 py-1.5 rounded-md transition-colors ${
                    preset === p.value
                      ? 'bg-[#c9a96e] text-white'
                      : 'bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-[#c9a96e]/50'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {preset === 'custom' && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Start Date</label>
                <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className={`${inputCls} w-full`} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">End Date</label>
                <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className={`${inputCls} w-full`} />
              </div>
            </>
          )}

          <MultiSelect label="Markets" options={MARKETS} selected={markets} onChange={setMarkets} />
        </div>
      </div>

      {/* Tabs */}
      <nav className="flex overflow-x-auto gap-1 border-b border-gray-200 dark:border-gray-800 mb-4 mt-2">
        {tabs.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              `px-3 py-2 text-sm whitespace-nowrap transition-colors ${
                isActive
                  ? 'border-b-2 border-[#c9a96e] text-[#c9a96e]'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {/* Pages */}
      <Routes>
        <Route index element={<MarketFlowsPage filterParams={filterParams} />} />
        <Route path="markets" element={<MarketFlowsPage filterParams={filterParams} />} />
        <Route path="sectors" element={<SectorFlowsPage filterParams={filterParams} />} />
        <Route path="tickers" element={<TickerDetailPage filterParams={filterParams} />} />
        <Route path="events" element={<IndexEventsPage />} />
        {isUploader && <Route path="ingestion" element={<IngestionPage />} />}
      </Routes>
    </div>
  );
};

export default GccFlowsLayout;
